import type { FixedLengthArray } from "models-webgl2";
import { arrayOf } from "utils/helpers/array-of";
import { makeAutoBindFunctions } from "utils/helpers/make-auto-bind-functions";
import { isFinitePositive } from "utils/math/is-finite-positive";
import { WorkerPromise, PromiseWorkerExecutor } from "./worker-promise";

type PromiseWorkerResolveCallback<O> = (message: O) => void;

type PromiseWorkerRejectCallback = (reason: any) => void;

export type MakeWorkerJobType<
  T extends (event: MessageEvent<any>) => any,
  W extends number,
> = T extends (event: MessageEvent<infer D>) => infer O ? WorkerJob<D, O, W> : never;

export class WorkerJob<T = any, O = any, A extends number = 1> {
  private readonly _workers: WorkerPromise<T, O>[];
  private readonly _workersAmount: number;

  private _resolves: PromiseWorkerResolveCallback<FixedLengthArray<O, A>>[] = [];
  private _rejects: PromiseWorkerRejectCallback[] = [];

  constructor(executor: PromiseWorkerExecutor<T, O>, workersAmount: A) {
    if (!isFinitePositive(workersAmount) && !Number.isInteger(workersAmount)) {
      throw new TypeError(
        "Invalid workersAmount is passed. Ensure that workersAmount is integer above zero.",
      );
    }

    this._workers = arrayOf(workersAmount, () => new WorkerPromise(executor));
    this._workersAmount = workersAmount;

    makeAutoBindFunctions(this);
  }

  public process(data: FixedLengthArray<T, A>): Promise<FixedLengthArray<O, A>> {
    const messages = arrayOf(this._workersAmount, (i) => {
      return this._workers[i].postMessage(data[i]);
    });

    return new Promise((resolve, reject) => {
      this._resolves.push(resolve);
      this._rejects.push(reject);
      Promise.all(messages).then(this._sendResult).catch(this._sendError);
    });
  }

  private _sendResult(res: any) {
    this._resolves.shift()?.(res);
    this._rejects.shift();
  }

  private _sendError(reason: any) {
    this._resolves.shift();
    this._rejects.shift()?.(reason);
  }
}

export default WorkerJob;
